import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN_KEY = 'cp_user_token';

const AuthContext = createContext({
  user: null,
  token: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  refreshUser: async () => {},
});

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async (overrideToken) => {
    const t = overrideToken || token;
    if (!t) { setUser(null); setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      setUser(r.data?.user || null);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  async function login(email, password) {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    const t = r.data?.token;
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setUser(r.data?.user || null);
    }
    return r.data;
  }

  async function register(email, password, name) {
    const r = await axios.post(`${API}/auth/register`, { email, password, name });
    const t = r.data?.token;
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setUser(r.data?.user || null);
    }
    return r.data;
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// Helper for axios calls that need authentication.
export function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}
